import { ToggleButton as FluentToggleButton } from "@fluentui/web-components";
/**
 * ToggleButton
 * @summary A ToggleButton Custom HTML Element based on Fluent UI's ToggleButton with custom styling and behavior.
 *
 * @example
 * ```html
 * <fabric-toggle-button appearance="primary" shape="rounded" size="large" type="submit" autofocus>
 *   <span slot="start">🚀</span>
 *   Toggle me!
 *   <span slot="end">→</span>
 * </fabric-toggle-button>
 * ```
 *
 * @attr {boolean | undefined} pressed - Indicates the pressed state of the control.
 * @attr {boolean | undefined} mixed - Indicates the mixed state of the control. This property takes precedence over `pressed`.
 * @attr {ToggleButtonAppearance | undefined} appearance - Indicates the styled appearance of the button.
 * @attr {ToggleButtonShape | undefined} shape - The shape of the button.
 * @attr {ToggleButtonSize | undefined} size - The size of the button.
 * @attr {boolean | undefined} icon-only - Indicates that the button should only display as an icon with no text content.
 * @attr {boolean | undefined} autofocus - Indicates the button should be focused when the page is loaded.
 * @attr {boolean | undefined} disabled - Sets the element's disabled state.
 * @attr {boolean | undefined} disabled-focusable - Indicates that the button is focusable while disabled.
 * @attr {number | undefined} tabindex - Sets that the button tabindex attribute.
 * @attr {string | undefined} form - The associated form element.
 * @attr {string | undefined} formaction - The URL that processes the form submission.
 * @attr {string | undefined} formenctype - The encoding type for the form submission.
 * @attr {string | undefined} formmethod - The HTTP method that the browser uses to submit the form.
 * @attr {boolean | undefined} formnovalidate - Indicates that the form will not be validated when submitted.
 * @attr {ToggleButtonFormTarget | undefined} formtarget - The target frame or window to open the form submission in.
 * @attr {string | undefined} name - The name of the element. This element's value will be surfaced during form submission under the provided name.
 * @attr {ToggleButtonType | undefined} type - The button type.
 * @attr {string | undefined} value - The value attribute.
 *
 * @prop {boolean} pressed - Indicates the pressed state of the control.
 * @prop {boolean} mixed - Indicates the mixed state of the control. This property takes precedence over `pressed`.
 * @prop {ToggleButtonAppearance} appearance - Indicates the styled appearance of the button.
 * @prop {ToggleButtonShape} shape - The shape of the button.
 * @prop {ToggleButtonSize} size - The size of the button.
 * @prop {boolean} icon-only - Indicates that the button should only display as an icon with no text content.
 * @prop {boolean} autofocus - Indicates the button should be focused when the page is loaded.
 * @prop {boolean} disabled - Sets the element's disabled state.
 * @prop {boolean} disabled-focusable - Indicates that the button is focusable while disabled.
 * @prop {number} tabindex - Sets that the button tabindex attribute.
 * @prop {string} form - The associated form element.
 * @prop {string} formaction - The URL that processes the form submission.
 * @prop {string} formenctype - The encoding type for the form submission.
 * @prop {string} formmethod - The HTTP method that the browser uses to submit the form.
 * @prop {boolean} formnovalidate - Indicates that the form will not be validated when submitted.
 * @prop {ToggleButtonFormTarget} formtarget - The target frame or window to open the form submission in.
 * @prop {string} name - The name of the element. This element's value will be surfaced during form submission under the provided name.
 * @prop {ToggleButtonType} type - The button type.
 * @prop {string} value - The value attribute.
 *
 * @slot start - Content which can be provided before the button content.
 * @slot end - Content which can be provided after the button content.
 * @slot - The default slot for button content.
 *
 * @csspart content - The button content container.
 *
 * @method press - Toggles the pressed state of the button.
 * @method keypressHandler - Handles keypress events for the button.
 * @method resetForm - Resets the associated form.
 *
 * @extends FluentToggleButton
 * @tagname fabric-toggle-button
 * @public
 */
export class ToggleButton extends FluentToggleButton {
}
//# sourceMappingURL=toggle-button.js.map